import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import {
  PricingTableFormValues,
  pricingTableFormSchema,
} from "@/schemas/pricingTableSchema";
import { useState } from "react";
import {
  pricingTableApi,
  feesApportionPartyApi,
  documentTypeApi,
} from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation } from "@tanstack/react-query";

const schema = pricingTableFormSchema;

type Schema = typeof schema;

interface UsePricingTableFormProps {
  pricingTableId?: number;
  initialData?: any;
}

const usePricingTableForm = ({
  pricingTableId,
  initialData,
}: UsePricingTableFormProps = {}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedRole } = useAuth();

  const form = useForm<PricingTableFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialData || {
      name: "",
      description: "",
      transaction_fees: [],
      document_fees: [],
      delivery_fees: [],
      custom_form_fees: [],
      bundles: [],
    },
    mode: "onChange",
  });

  const {
    fields: documentFeeFields,
    append: appendDocumentFee,
    remove: removeDocumentFee,
  } = useFieldArray({
    control: form.control,
    name: "document_fees",
  });

  const { fields: transactionFeeArray } = useFieldArray({
    control: form.control,
    name: "transaction_fees",
  });

  const { data: feeApportionParties = [], isLoading: loadingParties } =
    useQuery({
      queryKey: ["fee-apportion-parties"],
      queryFn: () => feesApportionPartyApi.list(),
    });

  const { data: documentTypes = [], isLoading: loadingDocumentTypes } =
    useQuery({
      queryKey: ["document-types"],
      queryFn: () => documentTypeApi.list(),
    });

  const updateMutation = useMutation({
    mutationFn: async (data: PricingTableFormValues) => {
      if (!pricingTableId) {
        throw new Error("Pricing table ID is required for update");
      }
      return pricingTableApi.update(pricingTableId, data);
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Pricing table updated successfully.",
      });
      navigate("/backoffice/pricing");
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: error.message,
      });
    },
  });

  const onSubmit = async (data: PricingTableFormValues) => {
    
    try {
      setIsSubmitting(true);
      console.log('Parsing form data with schema...');
      const formData = { ...schema.parse(data) };
      console.log('Parsed form data:', formData);
      

      console.log(
        "usePricingTableForm onSubmit called with selectedRole:",
        selectedRole
      );

      if (pricingTableId) {
        console.log('Updating existing pricing table...');
        await updateMutation.mutateAsync(formData);
        console.log('Update mutation completed successfully');
      } else {
        // For management company users, use the selected role's entity ID
        if (
          selectedRole?.type === "management_company" &&
          selectedRole.entityId
        ) {
          console.log(
            "Creating pricing table with management_company_id:",
            selectedRole.entityId
          );
          await pricingTableApi.create({
            ...formData,
            management_company_id: selectedRole.entityId,
          });
        } else {
          throw new Error(
            "Management company ID is required from selected role"
          );
        }

        toast({
          title: "Success!",
          description: "Pricing table created successfully.",
        });
        navigate("/backoffice/pricing");
      }
    } catch (error: any) {
      console.error('=== ERROR in usePricingTableForm onSubmit ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      
      console.error("Error in usePricingTableForm onSubmit:", error);
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: error.message,
      });
    } finally {
      console.log('=== usePricingTableForm onSubmit END ===');
      setIsSubmitting(false);
    }
  };

  const validateApportionTotals = (data: PricingTableFormValues) => {
    const errors: string[] = [];
    // Add validation logic here
    return errors;
  };

  return {
    form,
    onSubmit,
    isSubmitting,
    documentFeeFields,
    transactionFeeArray,
    feeApportionParties,
    documentTypes,
    loadingParties,
    loadingDocumentTypes,
    appendDocumentFee,
    removeDocumentFee,
    updateMutation,
    isEditing: !!pricingTableId,
    validateApportionTotals,
  };
};

export default usePricingTableForm;
